import React, { useState, useEffect, useRef, useCallback } from 'react'
import { priceFormat, showError } from '../utils/helper'
import { logCard } from '../utils/cardPaymentLog'

/*
 * Teya odbija otkazivanje kad je terminal karticu vec preuzeo (HTTP 409 NOT_CANCELLABLE,
 * ranije 500 s Teyinim 406). To je ocekivan ishod, ne kvar, pa ide u dijagnostiku kao
 * *_REJECTED — inace bi svaki takav klik slao kriticni mail o nepostojecem kvaru.
 */
const isCancelRefused = (err) => {
    const httpStatus = err?.response?.status
    const respData = err?.response?.data
    const errMsg = (typeof respData?.error === 'string' ? respData.error : '') || err?.message || ''
    return respData?.code === 'NOT_CANCELLABLE'
        || httpStatus === 409
        || httpStatus === 406
        || errMsg.includes('NOT_ACCEPTABLE')
        || errMsg.includes('cannot be cancelled')
}

const logCancelFailure = (baseType, err, data = {}) => {
    const refused = isCancelRefused(err)
    const respData = err?.response?.data
    const errMsg = (typeof respData?.error === 'string' ? respData.error : '') || err?.message || 'cancel failed'
    logCard(refused ? `${baseType}_REJECTED` : `${baseType}_ERR`, errMsg, {
        ...data,
        http_status: err?.response?.status,
        not_acceptable: refused,
    })
    return refused
}

const TeyaPaymentModal = ({ show, amount, onSuccess, onManualConfirm, onCancel, onClose }) => {
    const [status, setStatus] = useState('INIT') // INIT, NEW, IN_PROGRESS, SUCCESSFUL, FAILED, CANCELLED, TIMEOUT, ERROR
    const [elapsed, setElapsed] = useState(0)
    const [errorMessage, setErrorMessage] = useState('')
    const [transactionId, setTransactionId] = useState(null)
    const [showManualEntry, setShowManualEntry] = useState(false)
    const [manualTxId, setManualTxId] = useState('') // kod odobrenja s terminal-slipa
    const [manualConfirmed, setManualConfirmed] = useState(false)
    const [noSlipConfirmed, setNoSlipConfirmed] = useState(false) // kod odobrenja nije upisan — prikaži grešku
    const [cancelInFlight, setCancelInFlight] = useState(false)
    const [abandonConfirm, setAbandonConfirm] = useState(false)
    const [verifying, setVerifying] = useState(false)
    const [verifyError, setVerifyError] = useState('')      // zasto racun nije izdan
    const [candidates, setCandidates] = useState([])        // naplate na tocan iznos, za odabir
    const [verifyUnavailable, setVerifyUnavailable] = useState(false) // Teya ne odgovara
    const [abandonLeft, setAbandonLeft] = useState(0)       // koliko jos traje provjera
    const [foreignCharge, setForeignCharge] = useState(null) // terminal naplatio drugi iznos

    const paymentRequestIdRef = useRef(null)
    const pollTimeoutRef = useRef(null)
    const timerInterval = useRef(null)
    const timeoutRef = useRef(null)
    const isCancelling = useRef(false)
    const isFinishedRef = useRef(false)
    const consecutiveErrorsRef = useRef(0)
    const cancelRequestedRef = useRef(false)
    const abandonTimerRef = useRef(null)
    const abandonedRef = useRef(false)
    const abandonTickRef = useRef(null)
    const chargeWatchRef = useRef(null)
    const startedAtRef = useRef(null)

    const POLL_INTERVAL_MS = 2000
    const TIMEOUT_MS = 180000
    const MAX_CONSECUTIVE_POLL_ERRORS = 8
    // Koliko još čekamo ishod nakon što blagajnik prekine čekanje. Zahtjev koji terminal
    // nije dao otkazati može još završiti naplatom, pa se prozor ne smije zatvoriti
    // prije nego to provjerimo. Ovo je samo krajnji rok — naplatu na terminalu vidimo
    // kroz watchTerminalCharges() u par sekundi, pa dulje čekanje kupca nema svrhe.
    const VERIFY_AFTER_ABANDON_MS = 45000

    const cleanup = useCallback(() => {
        if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current)
        if (timerInterval.current) clearInterval(timerInterval.current)
        if (timeoutRef.current) clearTimeout(timeoutRef.current)
        if (abandonTimerRef.current) clearTimeout(abandonTimerRef.current)
        if (abandonTickRef.current) clearInterval(abandonTickRef.current)
        if (chargeWatchRef.current) clearTimeout(chargeWatchRef.current)
        pollTimeoutRef.current = null
        timerInterval.current = null
        timeoutRef.current = null
        abandonTimerRef.current = null
        abandonTickRef.current = null
        chargeWatchRef.current = null
    }, [])

    const finishWithSuccess = useCallback((data) => {
        if (isFinishedRef.current) return
        isFinishedRef.current = true
        setStatus('SUCCESSFUL')
        setTransactionId(data.transaction_id)
        logCard('SUCCESS', 'Plaćanje uspješno', {
            prId: paymentRequestIdRef.current,
            transaction_id: data.transaction_id,
            gateway_payment_id: data.gateway_payment_id,
        })
        cleanup()
        if (onSuccess) {
            onSuccess(data.transaction_id, data.gateway_payment_id)
        }
    }, [cleanup, onSuccess])

    const pollOnce = useCallback(async (prId) => {
        // NAMJERNO ne provjeravamo isCancelling.current — pollanje mora teći i dok je cancel API u letu,
        // jer ako Teya odbije cancel (NOT_ACCEPTABLE) transakcija ide dalje i može završiti SUCCESSFUL.
        if (!prId || isFinishedRef.current) return

        try {
            const { data } = await window.axios.get(`/card-processor/status/${prId}`)
            consecutiveErrorsRef.current = 0

            logCard('POLL', `status=${data?.status || 'n/a'} success=${data?.success}`, {
                prId,
                status: data?.status,
                success: data?.success,
                error: data?.error,
                transaction_id: data?.transaction_id,
                gateway_payment_id: data?.gateway_payment_id,
                status_reason: data?.status_reason,
            })

            if (!data.success) {
                if (isFinishedRef.current) return
                isFinishedRef.current = true
                setStatus('ERROR')
                setErrorMessage(data.error || 'Greška pri provjeri statusa')
                cleanup()
                return
            }

            if (data.status === 'SUCCESSFUL') {
                finishWithSuccess(data)
                return
            }

            if (data.status === 'FAILED' || data.status === 'CANCELLED') {
                setStatus(data.status)
                isFinishedRef.current = true
                logCard('TERMINAL', `Završetak sa statusom ${data.status}`, {
                    prId,
                    status: data.status,
                    status_reason: data.status_reason,
                })
                cleanup()
                return
            }

            // Intermediate status (NEW / IN_PROGRESS). Ako je blagajnik već zatražio otkaz koji
            // je terminal odbio, zadrži "ljepljivo" CANCEL_REJECTED (escape opcije ostaju vidljive)
            // umjesto da poll svakih 2s vrati prikaz natrag na spinner.
            if (abandonedRef.current) {
                setStatus('VERIFY_ABANDON')
            } else {
                setStatus(cancelRequestedRef.current ? 'CANCEL_REJECTED' : data.status)
            }

            // Schedule next poll
            if (!isFinishedRef.current) {
                pollTimeoutRef.current = setTimeout(() => pollOnce(prId), POLL_INTERVAL_MS)
            }
        } catch (err) {
            consecutiveErrorsRef.current += 1
            logCard('POLL_ERR', err?.message || 'network error', {
                prId,
                consecutive: consecutiveErrorsRef.current,
                http_status: err?.response?.status,
                response_data: err?.response?.data,
            })
            if (consecutiveErrorsRef.current >= MAX_CONSECUTIVE_POLL_ERRORS) {
                if (isFinishedRef.current) return
                isFinishedRef.current = true
                setStatus('ERROR')
                setErrorMessage('Gubitak veze s poslužiteljem. Provjerite stanje plaćanja na terminalu prije ponovnog pokušaja.')
                cleanup()
                return
            }
            // Network/transient error — keep polling
            if (!isFinishedRef.current) {
                pollTimeoutRef.current = setTimeout(() => pollOnce(prId), POLL_INTERVAL_MS)
            }
        }
    }, [cleanup, finishWithSuccess])

    /*
     * Dok terminal ne odgovara, status zahtjeva zna zapeti na IN_PROGRESS i blagajnik
     * bulji u brojac bez ijedne informacije. Zato paralelno gledamo sto je terminal
     * STVARNO naplatio: naplata na tocan iznos zakljucuje racun odmah, a naplata na
     * drugi iznos (terminal je zatvorio zaostali zahtjev) odmah se prijavi kao problem.
     */
    const watchTerminalCharges = useCallback(async () => {
        if (isFinishedRef.current) return

        try {
            const { data } = await window.axios.get('/card-processor/charges', {
                params: { since_minutes: 15 },
            })
            const startedAt = startedAtRef.current || 0
            const fresh = (data?.charges || []).filter((c) => {
                if (c.used_by_sale_id) return false
                const at = c.at ? Date.parse(c.at) : 0
                return at >= startedAt - 5000
            })

            const exact = fresh.find((c) => Math.round((c.amount || 0) * 100) === Math.round(amount * 100))
            if (exact) {
                logCard('CHARGE_FOUND', `Terminal naplatio ${exact.amount} EUR (kod ${exact.approval_code})`, {
                    prId: paymentRequestIdRef.current,
                    gateway_payment_id: exact.gateway_payment_id,
                    approval_code: exact.approval_code,
                })
                finishWithSuccess({
                    transaction_id: exact.transaction_id,
                    gateway_payment_id: exact.gateway_payment_id,
                })
                return
            }

            if (fresh.length > 0) {
                // Terminal je naplatio NEŠTO drugo — račun na ovaj iznos ne smije izaći.
                isFinishedRef.current = true
                cleanup()
                setForeignCharge(fresh[0])
                setStatus('CHARGE_MISMATCH')
                logCard('CHARGE_MISMATCH', `Terminal naplatio ${fresh[0].amount} EUR umjesto ${amount} EUR`, {
                    prId: paymentRequestIdRef.current,
                    charged: fresh[0].amount,
                    expected: amount,
                    approval_code: fresh[0].approval_code,
                })
                return
            }
        } catch (e) {
            logCard('CHARGE_WATCH_ERR', e?.message || 'provjera naplata nije uspjela', {
                http_status: e?.response?.status,
            })
        }

        if (!isFinishedRef.current) {
            chargeWatchRef.current = setTimeout(watchTerminalCharges, 5000)
        }
    }, [amount, cleanup, finishWithSuccess])

    const initiatePayment = useCallback(async () => {
        setStatus('INIT')
        setElapsed(0)
        setErrorMessage('')
        abandonedRef.current = false
        setTransactionId(null)
        setShowManualEntry(false)
        setManualTxId('')
        setManualConfirmed(false)
        setNoSlipConfirmed(false)
        setCancelInFlight(false)
        setAbandonConfirm(false)
        setVerifying(false)
        setVerifyError('')
        setCandidates([])
        setVerifyUnavailable(false)
        setAbandonLeft(0)
        setForeignCharge(null)
        paymentRequestIdRef.current = null
        isCancelling.current = false
        isFinishedRef.current = false
        consecutiveErrorsRef.current = 0
        cancelRequestedRef.current = false

        logCard('INIT', `Pokretanje plaćanja, iznos=${amount} EUR`, { amount })

        try {
            const { data } = await window.axios.post('/card-processor/payment', {
                amount: amount,
                currency: 'EUR',
            })

            logCard('INIT_RESP', `success=${data?.success} status=${data?.status} prId=${data?.payment_request_id}`, {
                success: data?.success,
                status: data?.status,
                payment_request_id: data?.payment_request_id,
                error: data?.error,
            })

            if (!data.success) {
                isFinishedRef.current = true
                setStatus('ERROR')
                setErrorMessage(data.error || 'Greška pri pokretanju plaćanja')
                return
            }

            const prId = data.payment_request_id
            paymentRequestIdRef.current = prId
            // Naplata nastala POSLIJE ovog trenutka na ovom terminalu je nasa.
            startedAtRef.current = Date.now()
            setStatus(data.status || 'NEW')

            // Start elapsed timer
            const startTime = Date.now()
            timerInterval.current = setInterval(() => {
                setElapsed(Math.floor((Date.now() - startTime) / 1000))
            }, 1000)

            // Start polling immediately (don't wait for first interval)
            pollOnce(prId)

            // Set hard timeout — but verify final status before declaring failure,
            // to avoid the race where the card was charged in the last seconds.
            timeoutRef.current = setTimeout(async () => {
                if (isFinishedRef.current) return
                logCard('TIMEOUT_CHECK', 'Hard timeout, finalna provjera statusa', { prId })
                try {
                    const { data: finalData } = await window.axios.get(`/card-processor/status/${prId}`)
                    logCard('TIMEOUT_RESP', `status=${finalData?.status}`, {
                        prId,
                        status: finalData?.status,
                        success: finalData?.success,
                        status_reason: finalData?.status_reason,
                    })
                    if (finalData?.success && finalData.status === 'SUCCESSFUL') {
                        finishWithSuccess(finalData)
                        return
                    }
                } catch (e) {
                    logCard('TIMEOUT_ERR', e?.message || 'network error', {
                        prId,
                        http_status: e?.response?.status,
                    })
                }
                if (isFinishedRef.current) return
                isFinishedRef.current = true
                setStatus('TIMEOUT')
                cleanup()
                window.axios.patch(`/card-processor/cancel/${prId}`).catch((e) => {
                    logCancelFailure('TIMEOUT_CANCEL', e, { prId })
                })
            }, TIMEOUT_MS)

        } catch (err) {
            isFinishedRef.current = true
            const msg = err.response?.data?.error || err.message || 'Greška pri komunikaciji s terminalom'
            logCard('INIT_ERR', msg, {
                http_status: err?.response?.status,
                response_data: err?.response?.data,
            })
            setStatus('ERROR')
            setErrorMessage(msg)
        }
    }, [amount, cleanup, pollOnce, finishWithSuccess])

    useEffect(() => {
        if (show && amount > 0) {
            initiatePayment()
        }

        return () => {
            cleanup()
        }
    }, [show]) // eslint-disable-line react-hooks/exhaustive-deps

    const handleCancel = async () => {
        if (isCancelling.current) return
        if (isFinishedRef.current) return
        isCancelling.current = true
        setCancelInFlight(true)
        cancelRequestedRef.current = true

        const prId = paymentRequestIdRef.current
        logCard('CANCEL_REQ', 'Korisnik klikne Otkaži', { prId })

        if (!prId) {
            // Nema payment request-a — siguran lokalni cancel
            cleanup()
            isFinishedRef.current = true
            setStatus('CANCELLED')
            isCancelling.current = false
            setCancelInFlight(false)
            return
        }

        try {
            await window.axios.patch(`/card-processor/cancel/${prId}`)
            // Pollanje moglo je u međuvremenu već završiti (SUCCESSFUL/FAILED) — ne diraj njegovu odluku.
            if (isFinishedRef.current) {
                isCancelling.current = false
                setCancelInFlight(false)
                return
            }
            logCard('CANCEL_OK', 'Plaćanje otkazano preko API-ja', { prId })
            cleanup()
            isFinishedRef.current = true
            setStatus('CANCELLED')
        } catch (err) {
            if (isFinishedRef.current) {
                // Pollanje je u međuvremenu donijelo finalni status — cancel rezultat nije relevantan
                isCancelling.current = false
                setCancelInFlight(false)
                return
            }
            logCancelFailure('CANCEL', err, { prId })

            // KLJUČNO: NE prekidamo pollanje. Transakcija na terminalu može još završiti
            // SUCCESSFUL — u tom slučaju će finishWithSuccess izdati račun. Polling samo nastavlja.
            setStatus('CANCEL_REJECTED')
        }

        isCancelling.current = false
        setCancelInFlight(false)
    }

    const handleRetry = () => {
        cleanup()
        initiatePayment()
    }

    // Izdaje ručni račun tek kad je naplata potvrđena kod Teye (ili je blagajnik
    // izričito preuzeo odgovornost jer Teya ne odgovara).
    const issueManual = (code, charge, unverified = false) => {
        isFinishedRef.current = true
        cleanup()
        logCard(unverified ? 'MANUAL_CONFIRM_UNVERIFIED' : 'MANUAL_CONFIRM', 'Blagajnik ručno izdao kartični račun', {
            prId: paymentRequestIdRef.current,
            prev_status: status,
            approval_code: code,
            gateway_payment_id: charge?.gateway_payment_id || null,
            verified: !unverified,
        })
        setStatus('SUCCESSFUL')
        if (onManualConfirm) {
            onManualConfirm(code, charge?.gateway_payment_id || null)
        }
    }

    /*
     * Račun se izdaje samo ako kod odobrenja pripada naplati na TOČAN iznos računa.
     * Bez te provjere je blagajna izdala račun na 28,90 dok je terminal naplatio 18,90
     * (kod prepisan sa starijeg slipa) — manjak se vidio tek u mjesečnom usklađenju.
     */
    const handleManualConfirm = async () => {
        if (isFinishedRef.current && status === 'SUCCESSFUL') return
        if (verifying) return
        const code = manualTxId.trim()
        // Kod odobrenja je OBAVEZAN. Ranije se praznim kodom moglo proći uz drugu potvrdu i
        // račun bi se zabilježio kao 'pos-blank' — bez ijednog podatka po kojem bi se naplata
        // kasnije mogla naći kod Teye, pa su takvi računi ostajali trajno neuskladivi.
        if (code === '') {
            setNoSlipConfirmed(true)
            return
        }

        // Teya ne odgovara i blagajnik je već upozoren — drugi klik izdaje uz zapis.
        if (verifyUnavailable) {
            issueManual(code, null, true)
            return
        }

        setVerifying(true)
        setVerifyError('')
        setCandidates([])

        try {
            const { data } = await window.axios.post('/card-processor/verify-approval', {
                approval_code: code,
                amount: amount,
            })
            setVerifying(false)
            logCard('MANUAL_VERIFY', `rezultat=${data?.result} kod=${code}`, {
                approval_code: code,
                result: data?.result,
                amount,
                charged: data?.payment?.amount,
            })

            if (data?.result === 'matched') {
                issueManual(data.payment?.approval_code || code, data.payment)
                return
            }

            setCandidates(data?.candidates || [])

            if (data?.result === 'amount_mismatch') {
                setVerifyError(`Kod ${code} pripada naplati od ${priceFormat(data.payment?.amount || 0)}, a račun je ${priceFormat(amount)}. Račun se ne može izdati.`)
            } else if (data?.result === 'already_used') {
                setVerifyError(`Ta naplata je već iskorištena na računu #${data.used_by_sale_id}. Ista naplata ne može pokriti dva računa.`)
            } else {
                setVerifyError(`Kod ${code} ne odgovara nijednoj naplati od ${priceFormat(amount)} na ovoj trgovini. Provjeri kod na slipu.`)
            }
        } catch (err) {
            setVerifying(false)
            const msg = err?.response?.data?.error || err?.message || 'Provjera nije uspjela'
            logCard('MANUAL_VERIFY_ERR', msg, {
                approval_code: code,
                http_status: err?.response?.status,
            })
            setVerifyUnavailable(true)
            setVerifyError('Provjera kod Teye nije moguća. Ako si SIGURAN da je kartica naplaćena upravo ovim iznosom, ponovi klik — račun će biti označen kao neprovjeren.')
        }
    }

    const handleAbandon = () => {
        // Prvi klik samo traži potvrdu — sprječava slučajni prekid prave transakcije.
        if (!abandonConfirm) {
            setAbandonConfirm(true)
            return
        }
        const prId = paymentRequestIdRef.current
        logCard('ABANDON', 'Blagajnik prekinuo čekanje (terminal ne reagira)', {
            prId,
            prev_status: status,
        })

        if (!prId) {
            isFinishedRef.current = true
            cleanup()
            if (onCancel) onCancel()
            return
        }

        // Prozor se NE zatvara i pollanje se NE gasi. Naplata koju terminal nije dao
        // otkazati može još proći; dok to ne provjerimo blagajnik ne smije dobiti poruku
        // da kartica nije naplaćena, niti račun zaključiti gotovinom.
        abandonedRef.current = true
        setAbandonConfirm(false)
        setStatus('VERIFY_ABANDON')

        window.axios.patch(`/card-processor/cancel/${prId}`)
            .then(() => logCard('ABANDON_CANCEL_OK', 'Otkazivanje prihvaćeno nakon prekida', { prId }))
            .catch((e) => logCancelFailure('ABANDON_CANCEL', e, { prId }))

        // Vidljivo odbrojavanje — blagajnik mora znati da provjera ima kraj.
        setAbandonLeft(Math.round(VERIFY_AFTER_ABANDON_MS / 1000))
        abandonTickRef.current = setInterval(() => {
            setAbandonLeft((left) => (left > 0 ? left - 1 : 0))
        }, 1000)

        watchTerminalCharges()

        abandonTimerRef.current = setTimeout(() => {
            if (isFinishedRef.current) return
            isFinishedRef.current = true
            cleanup()
            setStatus('UNVERIFIED')
            logCard('ABANDON_UNVERIFIED', 'Ishod naplate nije potvrđen u roku', { prId })
        }, VERIFY_AFTER_ABANDON_MS)
    }

    const handleClose = () => {
        cleanup()
        if (onClose) onClose()
    }

    const handleCancelAndClose = () => {
        cleanup()
        if (onCancel) onCancel()
    }

    if (!show) return null

    const isWaiting = ['INIT', 'NEW', 'IN_PROGRESS', 'CANCEL_REJECTED', 'VERIFY_ABANDON'].includes(status)
    const isFailed = ['FAILED', 'TIMEOUT', 'ERROR'].includes(status)
    // Ručno izdavanje računa dopuštamo kad je ishod NEPOZNAT (timeout / gubitak veze /
    // terminal ne reagira na otkazivanje), jer je tada kartica možda naplaćena a mi to ne znamo.
    // Kod FAILED je terminal jasno odbio karticu — tamo ručnu potvrdu NE nudimo.
    const canManual = ['TIMEOUT', 'ERROR', 'CANCEL_REJECTED', 'UNVERIFIED', 'VERIFY_ABANDON'].includes(status)

    return (
        <div className="fixed w-full h-screen left-0 top-0 z-50 flex flex-wrap justify-center content-center p-24">
            <div className="fixed w-full h-screen left-0 top-0 z-0 bg-black bg-opacity-60"></div>
            <div className="w-96 rounded-lg bg-white shadow-xl overflow-hidden z-10">
                <div className="p-6 text-center">
                    {/* Header */}
                    <div className="mb-4">
                        <h3 className="text-lg font-semibold text-gray-800">Kartično plaćanje</h3>
                        <p className="text-2xl font-bold text-gray-900 mt-1">{priceFormat(amount)}</p>
                    </div>

                    {/* Status display */}
                    <div className="my-6">
                        {isWaiting && (
                            <>
                                {/* Spinner */}
                                <div className="flex justify-center mb-4">
                                    <div className="w-12 h-12 border-4 border-gray-200 border-t-yellow-500 rounded-full animate-spin"></div>
                                </div>
                                <p className="text-gray-600 font-medium">
                                    {cancelInFlight && status !== 'CANCEL_REJECTED'
                                        ? 'Otkazujem plaćanje...'
                                        : (
                                            <>
                                                {status === 'INIT' && 'Pokretanje plaćanja...'}
                                                {status === 'NEW' && 'Čekanje na terminal...'}
                                                {status === 'IN_PROGRESS' && 'Plaćanje u tijeku...'}
                                                {status === 'CANCEL_REJECTED' && 'Otkazivanje s blagajne nije moguće — terminal procesira karticu.'}
                                                {status === 'VERIFY_ABANDON' && 'Provjeravam je li kartica ipak naplaćena...'}
                                            </>
                                        )}
                                </p>
                                {status === 'VERIFY_ABANDON' && (
                                    <>
                                        <p className="text-xs text-red-600 font-semibold mt-2 px-2">
                                            NE zaključuj račun na gotovinu dok ova provjera ne završi.
                                        </p>
                                        <p className="text-xs text-gray-500 mt-1 px-2">
                                            Provjeravam i što je terminal stvarno naplatio. Još <span className="font-semibold">{abandonLeft}s</span> — ako do tada ništa ne stigne, dobit ćeš opcije za nastavak. Kod odobrenja sa slipa možeš upisati i odmah, ispod.
                                        </p>
                                    </>
                                )}
                                {status === 'CANCEL_REJECTED' && (
                                    <p className="text-xs text-orange-500 mt-2 px-2">
                                        Za prekid pritisnite crveni <span className="font-semibold">STOP</span> gumb na <span className="font-semibold">terminalu</span> — čim terminal odgovori, prozor prikaže ishod (uspjeh → račun automatski; otkaz → "Plaćanje otkazano"). Ako terminal uopće ne reagira, koristite opcije ispod.
                                    </p>
                                )}
                                {/* Tijekom provjere nakon prekida ide odbrojavanje, ne rastuci brojac. */}
                                {status !== 'VERIFY_ABANDON' && (
                                    <p className="text-sm text-gray-400 mt-2">
                                        {elapsed}s
                                    </p>
                                )}
                            </>
                        )}

                        {status === 'SUCCESSFUL' && (
                            <>
                                <div className="flex justify-center mb-4">
                                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                                        <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                </div>
                                <p className="text-green-600 font-semibold">Plaćanje uspješno!</p>
                                {abandonedRef.current && (
                                    <p className="text-sm text-red-600 font-semibold mt-2 px-2">
                                        Kartica JE naplaćena unatoZ prekidu — raZun se izdaje kao kartiZni.
                                        Ne naplaćuj gotovinom.
                                    </p>
                                )}
                            </>
                        )}

                        {status === 'CANCEL_ON_TERMINAL' && (
                            <>
                                <div className="flex justify-center mb-4">
                                    <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center">
                                        <svg className="w-10 h-10 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                </div>
                                <p className="text-orange-600 font-bold text-lg">Pritisnite "OTKAZATI" na terminalu</p>
                            </>
                        )}

                        {status === 'CANCELLED' && (
                            <>
                                <div className="flex justify-center mb-4">
                                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                                        <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </div>
                                </div>
                                <p className="text-gray-600 font-semibold">Plaćanje otkazano</p>
                            </>
                        )}

                        {status === 'CHARGE_MISMATCH' && (
                            <>
                                <div className="flex justify-center mb-4">
                                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                                        <svg className="w-10 h-10 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                </div>
                                <p className="text-red-600 font-bold text-lg">Terminal je naplatio krivi iznos</p>
                                <p className="text-sm text-red-500 mt-2 px-2">
                                    Naplaćeno je <span className="font-semibold">{priceFormat(foreignCharge?.amount || 0)}</span>
                                    {foreignCharge?.approval_code ? ` (kod ${foreignCharge.approval_code})` : ''}, a račun je <span className="font-semibold">{priceFormat(amount)}</span>.
                                    Terminal je vjerojatno zatvorio stariju, nedovršenu naplatu. Račun NE izdavaj — napravi povrat na terminalu i naplati ponovno.
                                </p>
                            </>
                        )}

                        {status === 'UNVERIFIED' && (
                            <>
                                <div className="flex justify-center mb-4">
                                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                                        <svg className="w-10 h-10 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                </div>
                                <p className="text-red-600 font-bold text-lg">Ishod naplate NIJE potvrđen</p>
                                <p className="text-sm text-red-500 mt-2 px-2">
                                    Terminal nije javio ishod. Provjeri slip na terminalu prije nego račun
                                    zaključiš gotovinom — kartica je možda naplaćena.
                                </p>
                            </>
                        )}

                        {isFailed && (
                            <>
                                <div className="flex justify-center mb-4">
                                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                                        <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                </div>
                                <p className="text-red-600 font-semibold">
                                    {status === 'TIMEOUT' && 'Isteklo vrijeme čekanja'}
                                    {status === 'FAILED' && 'Plaćanje nije uspjelo'}
                                    {status === 'ERROR' && 'Greška'}
                                </p>
                                {errorMessage && (
                                    <p className="text-sm text-red-400 mt-1">{errorMessage}</p>
                                )}
                            </>
                        )}

                        {canManual && (
                            <div className="mt-5 pt-4 border-t border-gray-200 text-left">
                                {!showManualEntry ? (
                                    <button
                                        onClick={() => setShowManualEntry(true)}
                                        className="w-full py-2 px-4 bg-orange-100 hover:bg-orange-200 text-orange-800 border border-orange-300 rounded-lg font-medium transition-colors text-sm"
                                    >
                                        Kartica je ipak naplaćena — izdaj račun ručno
                                    </button>
                                ) : (
                                    <div>
                                        <p className="text-xs text-gray-500 mb-2">
                                            Provjerite na terminalu je li kartica naplaćena i upišite <span className="font-semibold">KOD ODOBRENJA</span> s Teya slipa (potvrda trgovca) pa potvrdite. Iznos: <span className="font-semibold">{priceFormat(amount)}</span>
                                        </p>
                                        <input
                                            type="text"
                                            value={manualTxId}
                                            onChange={e => { setManualTxId(e.target.value); if (noSlipConfirmed) setNoSlipConfirmed(false); setVerifyError(''); setCandidates([]); setVerifyUnavailable(false) }}
                                            placeholder="Kod odobrenja (npr. 002874)"
                                            className={"w-full px-3 py-2 text-sm border rounded-lg mb-3 focus:outline-none focus:ring-2 " + (noSlipConfirmed ? "border-red-400 focus:ring-red-400" : "focus:ring-orange-400")}
                                        />
                                        {noSlipConfirmed && (
                                            <p className="text-xs text-red-600 mb-3 -mt-1">
                                                Kod odobrenja je <span className="font-semibold">obavezan</span>. Nalazi se na Teya slipu (potvrda trgovca). Ako slip nije izašao, ponovite naplatu na terminalu ili naplatite gotovinom — račun bez koda se kasnije ne može uskladiti s Teyom.
                                            </p>
                                        )}
                                        {verifyError && (
                                            <p className={"text-xs mb-3 -mt-1 " + (verifyUnavailable ? "text-orange-600" : "text-red-600 font-semibold")}>
                                                {verifyError}
                                            </p>
                                        )}
                                        {candidates.length > 0 && (
                                            <div className="mb-3">
                                                <p className="text-xs text-gray-600 mb-1">
                                                    Naplate od {priceFormat(amount)} na ovoj trgovini — odaberi onu sa slipa:
                                                </p>
                                                {candidates.map((c) => (
                                                    <button
                                                        key={c.gateway_payment_id || c.payment_request_id}
                                                        onClick={() => issueManual(c.approval_code, c)}
                                                        className="w-full text-left px-3 py-2 mb-1 text-xs bg-green-50 hover:bg-green-100 border border-green-300 rounded-lg"
                                                    >
                                                        <span className="font-semibold">{priceFormat(c.amount)}</span>
                                                        {' · kod '}<span className="font-mono">{c.approval_code || '-'}</span>
                                                        {c.card_last4 ? ` · ****${c.card_last4}` : ''}
                                                        {c.at ? ` · ${String(c.at).substring(11, 16)} UTC` : ''}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                        <label className="flex items-start gap-2 text-xs text-gray-700 mb-3 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={manualConfirmed}
                                                onChange={e => setManualConfirmed(e.target.checked)}
                                                className="mt-0.5"
                                            />
                                            <span>Potvrđujem da je kartica naplaćena na terminalu i preuzimam odgovornost za ručno izdavanje računa.</span>
                                        </label>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => { setShowManualEntry(false); setNoSlipConfirmed(false) }}
                                                className="flex-1 py-2 px-4 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors text-sm"
                                            >
                                                Natrag
                                            </button>
                                            <button
                                                onClick={handleManualConfirm}
                                                disabled={!manualConfirmed || manualTxId.trim() === '' || verifying}
                                                className={"flex-1 py-2 px-4 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors text-sm " + (verifyUnavailable ? "bg-orange-600 hover:bg-orange-700" : "bg-green-600 hover:bg-green-700")}
                                            >
                                                {verifying ? 'Provjeravam kod Teye...' : (verifyUnavailable ? 'Izdaj bez provjere' : 'Izdaj račun')}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Action buttons */}
                    <div className="flex gap-2 mt-4">
                        {isWaiting && status !== 'CANCEL_ON_TERMINAL' && status !== 'CANCEL_REJECTED' && status !== 'VERIFY_ABANDON' && (
                            <button
                                onClick={handleCancel}
                                disabled={cancelInFlight}
                                className="flex-1 py-2 px-4 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {cancelInFlight ? 'Otkazujem...' : 'Otkaži'}
                            </button>
                        )}

                        {status === 'CANCEL_ON_TERMINAL' && (
                            <button
                                onClick={handleCancelAndClose}
                                className="flex-1 py-2 px-4 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors"
                            >
                                Zatvori
                            </button>
                        )}

                        {status === 'CANCEL_REJECTED' && (
                            <button
                                onClick={handleAbandon}
                                className={"flex-1 py-2 px-4 rounded-lg font-medium transition-colors text-sm border " + (abandonConfirm
                                    ? "bg-red-600 hover:bg-red-700 text-white border-red-600"
                                    : "bg-red-100 hover:bg-red-200 text-red-700 border-red-300")}
                            >
                                {abandonConfirm ? 'Potvrdi prekid i provjeri naplatu' : 'Terminal ne reagira — prekini'}
                            </button>
                        )}

                        {isFailed && (
                            <>
                                <button
                                    onClick={handleRetry}
                                    className="flex-1 py-2 px-4 bg-primary hover:bg-yellow-600 text-black rounded-lg font-medium transition-colors"
                                >
                                    Pokušaj ponovo
                                </button>
                                <button
                                    onClick={handleCancelAndClose}
                                    className="flex-1 py-2 px-4 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors"
                                >
                                    Odustani
                                </button>
                            </>
                        )}

                        {(status === 'UNVERIFIED' || status === 'CHARGE_MISMATCH') && (
                            <button
                                onClick={handleCancelAndClose}
                                className="flex-1 py-2 px-4 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors"
                            >
                                Zatvori
                            </button>
                        )}

                        {status === 'CANCELLED' && (
                            <button
                                onClick={handleCancelAndClose}
                                className="flex-1 py-2 px-4 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors"
                            >
                                Zatvori
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default TeyaPaymentModal
